import {useMemo} from 'react';
import type {DataLoadStatus} from 'api/types';
import {mapRtkToDataLoad} from 'store/api';
import {useAppSelector} from 'store/hooks';
import type {Company} from '../types';
import {companiesApi, useGetCompaniesByIdsQuery} from '../companiesApi';

// All args select the same cache entry, see CompaniesCache
const selectCompanies = companiesApi.endpoints.getCompaniesByIds.select({
  ids: [],
});

const noIds: string[] = [];

const useStableIds = (ids: string[]): string[] => {
  const key = ids.sort().join(',');
  return useMemo(() => (key ? key.split(',') : noIds), [key]);
};

/**
 * Loads the companies of the given product owners. Companies already loaded,
 * e.g. for another set of product owners, are taken from cache, and only the
 * missing product owners are fetched.
 * Skipped when no product owner ids are given.
 */
export const useCompanies = (
  productOwnerIds: string[] | undefined,
): DataLoadStatus<Company[]> => {
  const requestedIds = useStableIds(productOwnerIds ?? noIds);
  const cache = useAppSelector(selectCompanies).data;

  const missingIds = useStableIds(
    requestedIds.filter(id => !cache?.loadedIds.includes(id)),
  );

  const args = useMemo(() => ({ids: missingIds}), [missingIds]);
  const {status, error} = useGetCompaniesByIdsQuery(args, {
    skip: requestedIds.length === 0,
  });

  const companies = useMemo(() => {
    if (!cache) {
      return undefined;
    }

    const requested = new Set(requestedIds);
    return Object.values(cache.companiesById).filter(company =>
      requested.has(company.id),
    );
  }, [cache, requestedIds]);

  return useMemo(
    () =>
      requestedIds.length
        ? mapRtkToDataLoad(status, companies, error)
        : {status: 'NotLoaded'},
    [requestedIds.length, status, companies, error],
  );
};
